import { useContext, useEffect } from 'react';

import { recommendationsEndpoints } from '@/api/endpoints';
import { SocketContext } from '@/contexts/rtm';
import { onDialogCollectionUpdated } from '@/hooks/useDialogs';
import { onEntityCollectionUpdated } from '@/hooks/useEntities';
import { onIntentCollectionUpdated } from '@/hooks/useIntents';
import { SocketEvent } from '@/shared/types/rtm';

import { bind, useRtmBindings } from './rtmBindings';

/** Events delivered to the room of the open brain (see `subscribe_brain` below). */
export const brainBindings = [
  bind(SocketEvent.dialogs_created, (data, { rtm }) =>
    rtm.create('dialogs', data.brain_id, data.dialog_id)
  ),
  bind(SocketEvent.dialogs_updated, (data, { rtm }) =>
    rtm.update('dialogs', data.brain_id, data.dialog_id)
  ),
  bind(SocketEvent.dialogs_removed, (data, { rtm }) =>
    rtm.remove('dialogs', data.brain_id, data.dialog_id)
  ),
  bind(SocketEvent.dialogs_collection_updated, (data, { queryClient }) =>
    onDialogCollectionUpdated(queryClient, data.brain_id, data.collection, data.new_collection)
  ),

  bind(SocketEvent.intents_created, (data, { rtm }) =>
    rtm.create('intents', data.brain_id, data.intent)
  ),
  bind(SocketEvent.intents_updated, (data, { rtm }) =>
    rtm.update('intents', data.brain_id, data.intent)
  ),
  bind(SocketEvent.intents_removed, (data, { rtm }) =>
    rtm.remove('intents', data.brain_id, data.intent)
  ),
  bind(SocketEvent.intents_renamed, (data, { rtm }) =>
    rtm.rename('intents', data.brain_id, data.intent, data.new_intent)
  ),
  bind(SocketEvent.intents_collection_updated, (data, { queryClient }) =>
    onIntentCollectionUpdated(queryClient, data.brain_id, data.collection, data.new_collection)
  ),

  bind(SocketEvent.entities_created, (data, { rtm }) =>
    rtm.create('entities', data.brain_id, data.entity)
  ),
  bind(SocketEvent.entities_updated, (data, { rtm }) =>
    rtm.update('entities', data.brain_id, data.entity)
  ),
  bind(SocketEvent.entities_removed, (data, { rtm }) =>
    rtm.remove('entities', data.brain_id, data.entity)
  ),
  bind(SocketEvent.entities_renamed, (data, { rtm }) =>
    rtm.rename('entities', data.brain_id, data.entity, data.new_entity)
  ),
  bind(SocketEvent.entities_collection_updated, (data, { queryClient }) =>
    onEntityCollectionUpdated(queryClient, data.brain_id, data.collection, data.new_collection)
  ),

  bind(SocketEvent.webhooks_created, (data, { rtm }) =>
    rtm.create('webhooks', data.brain_id, data.webhook_id)
  ),
  bind(SocketEvent.webhooks_updated, (data, { rtm }) =>
    rtm.update('webhooks', data.brain_id, data.webhook_id)
  ),
  bind(SocketEvent.webhooks_removed, (data, { rtm }) =>
    rtm.remove('webhooks', data.brain_id, data.webhook_id)
  ),

  // Refetched rather than written from the payload, so it takes the same path as a first load
  bind(SocketEvent.recommendations_configured, (data, { queryClient }) =>
    queryClient.invalidateQueries({
      queryKey: [recommendationsEndpoints.recommendations(data.brain_id)],
    })
  ),

  bind(SocketEvent.versions_created, (data, { rtm }) =>
    rtm.create('versions', data.brain_id, data.version)
  ),
  bind(SocketEvent.versions_removed, (data, { rtm }) =>
    rtm.remove('versions', data.brain_id, data.version)
  ),
];

export const useRtmBrain = (brainId: string) => {
  const { socket, isConnected } = useContext(SocketContext);

  useRtmBindings(socket, brainBindings);

  useEffect(() => {
    if (!socket || !brainId || !isConnected) {
      return;
    }
    socket.emit(SocketEvent.subscribe_brain, { brain_id: brainId });
    return () => {
      socket.emit(SocketEvent.unsubscribe_brain, { brain_id: brainId });
    };
  }, [isConnected, brainId, socket]);
};
